import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppBuilderProvider } from "@/context/AppBuilderContext";
import { TrackerAccessProvider } from "@/context/TrackerAccessContext";
import { ConfirmProvider } from "@/context/ConfirmContext";
import { Toaster } from "sonner";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import CompanyDashboard from "@/pages/CompanyDashboard";
import Finance from "@/pages/Finance";
import Business from "@/pages/Business";
import WorkOrderEditor from "@/pages/WorkOrderEditor";
import MasterData from "@/pages/MasterData";
import AccountsReports from "@/pages/AccountsReports";
import Tracker from "@/pages/Tracker";
import TrackerProjectDetail from "@/pages/TrackerProjectDetail";
import UsersPage from "@/pages/Users";
import Settings from "@/pages/Settings";
import "@/index.css";

function Protected({ children, adminOnly }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading…</div>;
  if (!user) return <Navigate to="/login" state={{ from: loc }} replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/" replace />;
  return (
    <AppBuilderProvider>
      <TrackerAccessProvider>
        <Layout>{children}</Layout>
      </TrackerAccessProvider>
    </AppBuilderProvider>
  );
}

function AppRoutes() {
  const location = useLocation();
  // Synchronous check for OAuth callback (must run BEFORE ProtectedRoute /me check)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/" element={<Protected><CompanyDashboard /></Protected>} />
      <Route path="/finance" element={<Protected><Finance /></Protected>} />
      <Route path="/business" element={<Protected adminOnly><Business /></Protected>} />
      <Route path="/business/work-orders/:id" element={<Protected adminOnly><WorkOrderEditor /></Protected>} />
      <Route path="/reports" element={<Protected><AccountsReports /></Protected>} />
      <Route path="/tracker" element={<Protected><Tracker /></Protected>} />
      <Route path="/tracker/projects/:id" element={<Protected><TrackerProjectDetail /></Protected>} />
      <Route path="/masters" element={<Protected adminOnly><MasterData /></Protected>} />
      <Route path="/users" element={<Protected adminOnly><UsersPage /></Protected>} />
      <Route path="/settings" element={<Protected><Settings /></Protected>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <ConfirmProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </ConfirmProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
